import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const LocCtx = createContext(null);

const distM = (a, b) => {
  const R = 6371000, toR = (x) => (x * Math.PI) / 180;
  const dLat = toR(b[0] - a[0]), dLng = toR(b[1] - a[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a[0])) * Math.cos(toR(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};
export { distM };

/**
 * Ubicación exacta del usuario (GPS del navegador, alta precisión) + dirección (OSM Nominatim).
 * status: idle | prompt | locating | granted | denied | error | unsupported
 */
export function LocationProvider({ children }) {
  const supported = typeof navigator !== "undefined" && !!navigator.geolocation;
  const [status, setStatus] = useState(supported ? "idle" : "unsupported");
  const [coords, setCoords] = useState(null);
  const [place, setPlace] = useState(null);
  const [error, setError] = useState(null);
  const watchId = useRef(null);
  const lastGeo = useRef(null);
  const hasCoords = useRef(false);

  const geocode = useCallback(async (lat, lng) => {
    if (lastGeo.current && distM(lastGeo.current, [lat, lng]) < 40) return;
    lastGeo.current = [lat, lng];
    try { const { data } = await api.get("/geo/reverse", { params: { lat, lng } }); setPlace(data); } catch { /* sin dirección */ }
  }, []);

  const onPos = useCallback((p) => {
    const c = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: Math.round(p.coords.accuracy),
      altitude: p.coords.altitude, speed: p.coords.speed, at: new Date(p.timestamp) };
    hasCoords.current = true;
    setCoords(c); setStatus("granted"); setError(null);
    geocode(c.lat, c.lng);
  }, [geocode]);

  const onErr = useCallback((e) => {
    if (e.code === 1) { setStatus("denied"); setError("Permiso de ubicación denegado"); return; }
    if (!hasCoords.current) { setStatus("error"); setError(e.code === 3 ? "Tiempo de espera agotado al obtener GPS" : "Ubicación no disponible"); }
  }, []);

  const request = useCallback(() => {
    if (!supported) { setStatus("unsupported"); return; }
    if (!hasCoords.current) setStatus("locating");
    navigator.geolocation.getCurrentPosition(onPos, onErr, { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 });
    if (watchId.current == null) {
      watchId.current = navigator.geolocation.watchPosition(onPos, onErr, { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 });
    }
  }, [supported, onPos, onErr]);

  useEffect(() => {
    if (!supported) return undefined;
    let perm;
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" }).then((p) => {
        perm = p;
        if (p.state === "granted") request();
        else if (p.state === "denied") setStatus("denied");
        else setStatus("prompt");
        p.onchange = () => { if (p.state === "granted") request(); else if (p.state === "denied") setStatus("denied"); };
      }).catch(() => setStatus("prompt"));
    } else setStatus("prompt");
    return () => {
      if (perm) perm.onchange = null;
      if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    };
  }, [supported, request]);

  const position = coords ? [coords.lat, coords.lng] : null;
  return <LocCtx.Provider value={{ status, coords, position, place, error, request }}>{children}</LocCtx.Provider>;
}

export const useUserLocation = () => useContext(LocCtx);

export const placeLabel = (place) => {
  if (!place) return null;
  const street = [place.road, place.house_number].filter(Boolean).join(" ");
  return [street, place.suburb, place.city].filter(Boolean).join(", ") || place.display;
};
